import React, { useState } from "react";
import { Box } from "@mui/material";
import { Button } from "primereact/button";
import EzTable from "../../../../common/EzTable";
import EzNotFound from "../../../../common/EzNotFound";
import { DATE_STRATEGY } from "../../../../utils/date";
import { useStyles } from "../../Affiliate/style";
import AddPaymentDialog from "./AddPaymentDialog";
import ViewPaymentDialog from "./EditPaymentDialog";

const getIconPath = (imgPathDelta = "/no-record.png") =>
  process.env.PUBLIC_URL + imgPathDelta;

const InvoicePayments = ({ 
  paymentData, 
  paginationInfo,
  isLoading, 
  invoiceId, 
  onAddPayment,
  onPageChange,
  isSubmittingPayment,
  isPageLevelError,
  invoiceSummary,
}) => {
  const classes = useStyles();
  const [showAddPaymentDialog, setShowAddPaymentDialog] = useState(false);
  const [showEditPaymentDialog, setShowEditPaymentDialog] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "-";
    return `$${parseFloat(value).toFixed(2)}`;
  };

  const enhancedColumns = paymentData?.columns?.map((col) => {
    if (col.dataKey === "paidAmount") {
      return {
        ...col,
        extraProps: {
          ...col.extraProps,
          body: (rowData) => formatCurrency(rowData.paidAmount),
        },
      };
    }
    if (col.dataKey === "additionalInfo") {
      return {
        ...col,
        extraProps: {
          ...col.extraProps,
          body: (rowData) => rowData.additionalInfo || "-",
        },
      };
    }
    if (col.extraProps?.isDateField && col.extraProps?.strategy) {
      return {
        ...col,
        extraProps: {
          ...col.extraProps,
          strategy: DATE_STRATEGY.DATE_ONLY,
        },
      };
    }
    return col;
  }) || [];

  const renderPageHeaderSection = () => {
    return (
      <div className="make-relative sub-header">
        <div className="maintitlediv">
          <div className="ml-3">
            <h4 className="pagetitle">Invoices Payments</h4>
          </div>
          <div className="buttonarea">
            <Button
              label="Add Payment"
              icon="pi pi-plus"
              className="p-button-raised"
              onClick={() => setShowAddPaymentDialog(true)}
              style={{ marginRight: "10px" }}
            />
          </div>
        </div>
      </div>
    );
  };

  const shouldRenderFullPageError = () => isPageLevelError;
  const shouldRenderRecords = () => paymentData?.rows && paymentData.rows.length > 0;
  const shouldRenderNotFoundView = () =>
    !shouldRenderRecords() && !shouldRenderFullPageError() && !isLoading;

  const handleAddPayment = (payload) => {
    onAddPayment(payload);
    setShowAddPaymentDialog(false);
  };

  const handleViewClick = (id) => {
    const payment = paymentData?.rows?.find((r) => r.id === id) || null;
    setSelectedPayment(payment);
    setShowEditPaymentDialog(true);
  };

  return (
    <div className={`containerbox ${classes.bg}`}>
      {renderPageHeaderSection()}

      <div className="mTop15">
        {shouldRenderFullPageError() && (
          <div style={{ textAlign: "center", padding: "10px", color: "#6c757d" }}>
            <h3>Oops! Something broke, we are working on a fix.</h3>
          </div>
        )}

        {shouldRenderRecords() && (
          <Box>
            <EzTable
              columns={enhancedColumns}
              value={paymentData.rows}
              emptyMessage="No payments found"
              areFiltersVisible={false}
              paginationInfo={{
                pageSize: paginationInfo?.size || 10,
                pageNumber: paginationInfo?.number ? paginationInfo.number - 1 : 0,
                totalPages: paginationInfo?.totalPages || 1,
                totalRecords: paginationInfo?.totalElements || paymentData.rows.length,
              }}
              screenPermissions={{ view: true, edit: false, create: false }}
              loading={isLoading}
              showMoreActions={false}
              showViewButton={true}
              onViewRecord={handleViewClick}
              showCopyButton={false}
              showDeleteButton={false}
              showEditButton={false}
              setDeleteConfirmationPopupVisibility={() => {}}
              loadLazyData={(params) => {
                if (onPageChange) {
                  const page = (params.page !== undefined ? params.page : params.pageNumber || 0) + 1;
                  const size = params.rows || params.pageSize || 10;
                  onPageChange(page, size);
                }
              }}
            />
          </Box>
        )}

        {shouldRenderNotFoundView() && (
          <div style={{ textAlign: "center", padding: "10px", color: "#6c757d" }}>
            <h3>No Invoice Payment records to show</h3>
          </div>
        )}
      </div>

      <AddPaymentDialog
        visible={showAddPaymentDialog}
        onHide={() => setShowAddPaymentDialog(false)}
        onSubmit={handleAddPayment}
        invoiceId={invoiceId}
        isSubmitting={isSubmittingPayment}
        invoiceSummary={invoiceSummary}
      />

      <ViewPaymentDialog
        visible={showEditPaymentDialog}
        onHide={() => {
          setShowEditPaymentDialog(false);
          setSelectedPayment(null);
        }}
        payment={selectedPayment}
      />
    </div>
  );
};

export default InvoicePayments;
